import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

/**
 * Loads the branch list once and shapes it for a filter picker.
 *
 * The option value is the branch CODE, not the branch name. Officer rows carry
 * a denormalised copy of the branch name, so filtering by name silently misses
 * leads whenever a branch has been renamed since the officer was created. The
 * code is the stable key and is present on both the officer and branch tables.
 *
 * Returns:
 *   branches              - raw rows, for callers that need to match on name
 *   branchOptions         - [{ label: "Name (CODE)", value: "CODE" }], by name
 *   normalizeBranchValues - maps saved branch NAMES onto their codes, so a
 *                           filter persisted before the switch to codes still
 *                           lines up with the options (see below)
 */
const useBranchOptions = () => {
    const [branches, setBranches] = useState([]);

    useEffect(() => {
        const tokens = JSON.parse(sessionStorage.getItem("tokens")) || null;
        const authToken = tokens?.token || null;
        if (!authToken) return;

        axios
            .get(`${process.env.REACT_APP_API_BASE_URL}/officer/get-branch-report?page=1&limit=2000`, {
                headers: { Authorization: `Bearer ${authToken}` },
            })
            .then((res) => setBranches(res?.data?.data?.branch_data || []))
            .catch((err) => console.error("Failed to load branch list", err));
    }, []);

    const branchOptions = useMemo(
        () =>
            branches
                .filter((b) => b?.branch_name && b?.branch_code)
                .slice()
                .sort((a, b) => a.branch_name.localeCompare(b.branch_name))
                .map((b) => ({
                    label: `${b.branch_name.trim()} (${b.branch_code.trim()})`,
                    value: b.branch_code.trim(),
                })),
        [branches],
    );

    /**
     * Branch filters used to be stored as branch names. Those values no longer
     * match any option value, so the field renders with nothing ticked while the
     * filter is still live: the user can't untick it, and ticking the same branch
     * appends its code alongside the stale name, filtering on two branches at
     * once. Mapping saved names onto codes keeps old filters selectable.
     *
     * Returns the input untouched until the branch list has loaded, so a filter
     * is never silently dropped just because the request is still in flight.
     */
    const normalizeBranchValues = useCallback(
        (values) => {
            const list = Array.isArray(values) ? values : values ? [values] : [];
            if (!branches.length || !list.length) return list;

            const codes = list.map((value) => {
                const key = String(value).trim().toLowerCase();
                const match = branches.find(
                    (b) =>
                        String(b?.branch_code ?? "").trim().toLowerCase() === key ||
                        String(b?.branch_name ?? "").trim().toLowerCase() === key,
                );
                return match ? String(match.branch_code).trim() : value;
            });

            // A name and its code may both be present; keep one entry per branch.
            return [...new Set(codes)];
        },
        [branches],
    );

    return { branches, branchOptions, normalizeBranchValues };
};

export default useBranchOptions;
